/**
 * KYN behaviour events (KYN-011, analytics/analytics-plan.md §4).
 *
 * Listens on the document (no edits inside the components) and publishes custom events through
 * Shopify's Web Pixels API as `kyn_<name>`. Shopify passes them only to pixels the visitor has
 * consented to; analytics/kyn-custom-pixel.js (Admin → Customer events) forwards them to GA4.
 * Standard commerce events (view_item, add_to_cart, begin_checkout, purchase) come from the
 * Google & YouTube app and are never sent from here, so nothing is counted twice.
 *
 * Every event carries the page context from snippets/kyn-analytics.liquid: page_type, language,
 * product_handle and drop on product pages, collection_handle on collections.
 */

/** @type {Record<string, unknown>} */
const context = (() => {
  try {
    return JSON.parse(document.getElementById('kyn-analytics-context')?.textContent ?? '{}');
  } catch {
    return {};
  }
})();

/** @type {Array<[string, Record<string, unknown>]>} */
const queue = [];

/**
 * @param {string} name
 * @param {Record<string, unknown>} [data]
 */
function publish(name, data = {}) {
  const payload = { ...context, ...data };
  const analytics = /** @type {any} */ (window).Shopify?.analytics;
  if (typeof analytics?.publish === 'function') {
    analytics.publish(`kyn_${name}`, payload);
  } else {
    // The pixel manager loads after the theme scripts on a slow connection: send once it is there.
    queue.push([name, payload]);
  }
}

function flushQueue() {
  const analytics = /** @type {any} */ (window).Shopify?.analytics;
  if (typeof analytics?.publish !== 'function') return false;
  for (const [name, payload] of queue.splice(0)) analytics.publish(`kyn_${name}`, payload);
  return true;
}

/*
 * Events fired by a click that leaves the page (pairs, colour links, filter chips, sorting) are kept
 * in sessionStorage and published from the next page: the pixel may not get to send them before the
 * page unloads. The event keeps the context of the page it happened on.
 */
const NEXT_PAGE_KEY = 'kyn:analytics:next';

/**
 * @param {string} name
 * @param {Record<string, unknown>} [data]
 */
function publishOnNextPage(name, data = {}) {
  try {
    const pending = JSON.parse(sessionStorage.getItem(NEXT_PAGE_KEY) ?? '[]');
    pending.push([name, { ...context, ...data }]);
    sessionStorage.setItem(NEXT_PAGE_KEY, JSON.stringify(pending.slice(-10)));
  } catch {
    publish(name, data);
  }
}

/** Publishes the previous page's navigation events once this page has loaded (pixels are ready by then). */
function publishPending() {
  try {
    const pending = JSON.parse(sessionStorage.getItem(NEXT_PAGE_KEY) ?? '[]');
    sessionStorage.removeItem(NEXT_PAGE_KEY);
    for (const [name, payload] of pending) queue.push([name, payload]);
    flushQueue();
  } catch {
    // Storage blocked: navigation events from the previous page are lost, nothing else is affected.
  }
}
if (document.readyState === 'complete') publishPending();
else window.addEventListener('load', publishPending, { once: true });

if (!flushQueue()) {
  let tries = 0;
  const timer = setInterval(() => {
    if (flushQueue() || ++tries > 40) clearInterval(timer);
  }, 250);
}

/** @param {string | null | undefined} href */
function handleFromHref(href) {
  const match = href?.match(/\/products\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

// Size and colour on the product page (main variant picker only, not cards or quick add).
document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement)) return;

  if (input.type === 'radio' && input.closest('variant-picker[data-template-product-match="true"]')) {
    const isColor = Boolean(input.closest('.variant-option--swatches'));
    publish(isColor ? 'select_color' : 'select_size', {
      option_name: input.dataset.optionName ?? null,
      option_value: input.value,
      variant_id: input.dataset.variantId ?? null,
      available: input.dataset.optionAvailable !== 'false',
    });
    return;
  }

  // Fabric facets (search page; the catalog uses the KYN chip bar below): results update in place.
  if (input.name === 'sort_by') {
    publish('sort_products', { sort_by: input.value });
  } else if (input.name.startsWith('filter.')) {
    publish('filter_products', {
      filter_name: input.name,
      filter_value: input.type === 'checkbox' || input.type === 'radio' ? input.value : String(input.value),
      checked: input.type === 'checkbox' ? input.checked : true,
    });
  }
});

// Sorting <select> (catalog chip bar submits on change; capture runs before its inline handler).
document.addEventListener(
  'change',
  (event) => {
    const select = event.target;
    if (select instanceof HTMLSelectElement && select.name === 'sort_by') publishOnNextPage('sort_products', { sort_by: select.value });
  },
  { capture: true }
);

document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // Colour swatches that link the products of one model (snippets/kyn-color-siblings.liquid).
    const colorLink = target.closest('.kyn-colors__link');
    if (colorLink instanceof HTMLAnchorElement) {
      if (colorLink.getAttribute('aria-current') !== 'true') {
        publishOnNextPage('click_color_sibling', {
          to_handle: handleFromHref(colorLink.getAttribute('href')),
          color: colorLink.title,
          placement: colorLink.closest('.kyn-colors--card') ? 'card' : 'product_page',
          from_handle: handleFromHref(colorLink.closest('product-card')?.querySelector('.product-card__link')?.getAttribute('href')) ?? context.product_handle ?? null,
        });
      }
      return;
    }

    // "Pasuje do" (product page recommendations).
    const pairLink = target.closest('product-recommendations a[href*="/products/"]');
    if (pairLink) {
      const items = [...document.querySelectorAll('product-recommendations .resource-list__item')];
      const item = pairLink.closest('.resource-list__item');
      publishOnNextPage('click_pairs', {
        to_handle: handleFromHref(pairLink.getAttribute('href')),
        position: item ? items.indexOf(item) + 1 : null,
      });
      return;
    }

    // Catalog chip bar (sections/kyn-collection-chips.liquid): filter chips and sizes are links.
    const chip = target.closest('.kyn-chip-bar a.kyn-pill, .kyn-chip-bar a.kyn-chip-size');
    if (chip) {
      publishOnNextPage('filter_products', {
        filter_name: chip.classList.contains('kyn-chip-size') ? 'size' : 'chip',
        filter_value: chip.textContent?.trim() ?? '',
        checked: chip.getAttribute('aria-pressed') !== 'true',
      });
      return;
    }

    // Size guide and the "Znajdź rozmiar" pick.
    if (target.closest('[data-sg-open]')) {
      publish('open_size_guide');
      return;
    }
    const pick = target.closest('[data-sg-pick]');
    if (pick) {
      publish('fit_finder_pick_size', { size: pick.textContent?.trim().split(/\s+/).pop() ?? null });
      return;
    }

    // Wishlist: product page button and card hearts. aria-pressed is read before the click flips it.
    const wish = target.closest('.kyn-wish__button, kyn-wishlist-toggle button');
    if (wish) {
      const adding = wish.getAttribute('aria-pressed') !== 'true';
      publish(adding ? 'add_to_wishlist' : 'remove_from_wishlist', {
        product_handle:
          handleFromHref(wish.closest('product-card')?.querySelector('.product-card__link')?.getAttribute('href')) ??
          context.product_handle ??
          null,
        placement: wish.closest('product-card') ? 'card' : 'product_page',
      });
      return;
    }

    // Social profile links (footer). They open in a new tab, so this page stays and sends the event.
    const social = target.closest('a[href*="instagram.com"], a[href*="tiktok.com"], a[href*="youtube.com"], a[href*="facebook.com"]');
    if (social instanceof HTMLAnchorElement) {
      publish('click_social', {
        network: social.hostname.replace(/^www\./, '').split('.')[0],
        placement: social.closest('footer, .footer, [id*="footer"]') ? 'footer' : 'page',
      });
    }
  },
  { capture: true }
);

// "Znajdź rozmiar": the answer form; the recommended size is read once the result is shown.
document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-sg-find]')) return;
  setTimeout(() => {
    const pick = document.querySelector('[data-sg-pick]');
    const recommended = pick && !pick.hasAttribute('hidden') ? pick.textContent?.trim().split(/\s+/).pop() : null;
    publish('use_fit_finder', { recommended_size: recommended ?? null });
  }, 0);
});

// Full-screen photo view (zoom dialog) opened. Watched on the whole page: the gallery is replaced
// when the variant changes.
new MutationObserver((records) => {
  for (const record of records) {
    const dialog = record.target;
    if (dialog instanceof HTMLDialogElement && dialog.open && dialog.closest('zoom-dialog')) {
      publish('view_gallery_fullscreen');
    }
  }
}).observe(document.body, { attributes: true, attributeFilter: ['open'], subtree: true });

// Sign-ups: customer forms reload the page, so success is read from the page that comes back.
if (document.querySelector('[id^="Email-signup__message-success-"]')) {
  publish('newsletter_signup');
}
for (const message of document.querySelectorAll('.kyn-restock__message[role="status"], .kyn-waitlist-prompt__message[role="status"]')) {
  publish('notify_me_signup', { placement: message.classList.contains('kyn-restock__message') ? 'restock_band' : 'product_page' });
}

// Listeners are attached (used by qa/analytics/events.cjs).
document.documentElement.dataset.kynAnalytics = 'ready';
